import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import * as _ from "lodash-es";
import { ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { SubSink } from "subsink";
import { ToastService } from "../shared/toast/toast.service";
import { IEntity } from "../shared/models/entity";
import { RosterProjectService } from "../services/roster-project.service";
import { IRosterProject } from "../shared/models/roster/roster-project";
import { RosterProjectFormComponent } from "./roster-project-form.component";
import { Role } from "../shared/models/role";
import { AuthenticationService } from "../services/authentication.service";
import { BusinessOperation } from "../shared/models/business-operation";


@Component({
  selector: "pps-manage-roster-project",
  templateUrl: "./manage-roster-project.component.html",
  styles: [`
    .roster-project .p-fieldset{
      border: 1px solid #aaaaaa !important;
      -webkit-box-shadow: 4px 4px 10px 0px rgba(64,64,64,0.3) !important;
      -moz-box-shadow:    4px 4px 10px 0px rgba(64,64,64,0.3) !important;
      box-shadow:         4px 4px 10px 0px rgba(64,64,64,0.3) !important;      
    }

    .roster-project .p-fieldset.inactive .p-fieldset-legend-text{
      color: #ffaaaa;
    }

    .roster-project .p-fieldset.active .p-fieldset-legend-text{
      font-weight: bold;
    }
  `],
  encapsulation: ViewEncapsulation.None
})
export class ManageRosterProjectComponent implements OnInit{
  @Input() workGroups: IEntity[] = [];
  @Input() roles: Role[] = [];
  @Input() rosterProjects: IRosterProject[] = [];

  private subs = new SubSink();
  smallScreen: boolean = false;

  collapseAll: boolean = true;
  workGroupsDict: {[id: number]: IEntity} = {};
  rolesDict: {[id: number]: Role} = {};
   

  constructor(
    private _confirm: ConfirmationService,
    private _toast: ToastService,
    private _dialog: DialogService,
    private _rosterProjectService: RosterProjectService,
    private _authService: AuthenticationService,
    private breakpointObserver: BreakpointObserver){
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });          
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }   

  loadData(){
    this.workGroupsDict = {};
    this.rolesDict = {};

    var workGroups = _.cloneDeep(this.workGroups);
    workGroups.forEach(wg => {
      wg.name = wg.active ? wg.name : `* ${wg.name} (Inactive)`;
    });

    workGroups.forEach(rg => this.workGroupsDict[rg.id] = rg);
    this.workGroups = workGroups.sort((a: IEntity,b: IEntity) => a.sortNo - b.sortNo);
    
    this.roles.forEach(r => this.rolesDict[r.id] = r);
  }

  deleteRosterProject(rosterProject: IRosterProject){
    this._confirm.confirm({
      key: "rsl-dialog",
      header: `Delete Roster Project`,
      message: `Please confirm that you want to delete [${rosterProject.title}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteRosterProjectHelper(rosterProject)
    });
  }

  deleteRosterProjectHelper(rosterProject: IRosterProject){
    this.subs.sink = this._rosterProjectService.delete(rosterProject.id)
      .subscribe(res=>{
        if (res.succeed){
          this._toast.succeeded(`${rosterProject.title} has been removed.`);
          let index = this.rosterProjects.findIndex(rsl => rsl.id === rosterProject.id);
          if (index >= 0){
            this.rosterProjects.splice(index, 1);
          }
        }else{
          this._toast.failed(`Failed to remove ${rosterProject.title}`);
        }
      });    
  }

  deleteGroup(rslIndex:number, rslId: number, workGroupId: number){
    let groupName = this.workGroupsDict[workGroupId].name;

    this._confirm.confirm({
      key: "rsl-dialog",
      header: `Detach Work Group`,
      message: `Please confirm that you want to detach [${groupName}] from [${this.rosterProjects[rslIndex].title}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteGroupHelper(rslIndex, rslId, workGroupId)
    });
  }

  private deleteGroupHelper(rslIndex:number, rslId: number, groupId: number){
    let rsl = {
      id: rslId,
      groupIds: [groupId]
    };
    let groupName = this.workGroupsDict[groupId].name;
    
    this.subs.sink = this._rosterProjectService.deleteGroup(rsl)
      .subscribe(res=>{
        if (res.succeed){
          this._toast.succeeded(`Work group ${groupName} detached.`);
          let index = this.rosterProjects[rslIndex].groupIds.findIndex(id => id === groupId);
          if (index >= 0){
            this.rosterProjects[rslIndex].groupIds.splice(index, 1);
          }
        }else{
          this._toast.failed(`Failed to detach ${groupName}`);
        }
      });
  }

  addOrUpdateRosterProject(rosterProject: IRosterProject){
    let create = rosterProject === null;

    let dialog = this._dialog.open(RosterProjectFormComponent, {
      header: `Define Roster Project`,
      styleClass: "size-md",
      data:{
        rosterProject: rosterProject || {id: 0, title: "", active: true, groupIds: []},
        workGroups: this.workGroups,
        roles: this.roles
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      result => {
        if (result){
          if (!create){
            this.subs.sink = this._rosterProjectService.update(result)
              .subscribe(res => {
                if (res.succeed){
                  var index = this.rosterProjects.findIndex(rt=>rt.id === res.result.id);
                  if (index !== -1){
                    this.rosterProjects[index] = _.cloneDeep(res.result);
                  }

                  this._toast.succeeded(`${rosterProject.title} updated.`);
                }else{
                  this._toast.failed(`Add new roster summary line failed: ${res.errors.join(";")}`);
                }
              });
          }else{
            this.subs.sink = this._rosterProjectService.add(result)
              .subscribe(res => {
                if (res.succeed){
                  this.rosterProjects.unshift(_.cloneDeep(res.result));
                  this._toast.succeeded(`Roster type ${res.result.title} created.`);
                }else{
                  this._toast.failed(`Update failed: ${res.errors.join(";")}`);
                }
              });
          }
        }
      },
      reason => {
      }
    );
  }  

  /**
   * Check if current user has permission to config roster project permission.
   * @returns true with permission, false without.
   */
   canSetPermission(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageRosterProjectPermission);
  }
}
